import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  IOutgoingPrintRenderRequest,
  IOutgoingPrintSource,
  IOutgoingPrintSources,
  IPrintFormat,
  IPrintProfile,
  IPrintSettings,
  ISavePrintFormat,
  ISavePrintProfile,
} from "dto/Printing/IOutgoingPrint";

// Drukowanie przesyłek wychodzących (port ModalPrintManager z Magistratu):
// ustawienia firmy (formaty naklejek/kopert, profile wydruku) + źródła wydruku i PDF-y
// naklejek, kopert i zwrotek ZPO generowane na serwerze. Pisma deszyfruje FE.

export const printQueryKeys = {
  settings: ["print.settings"],
  sources: (source: IOutgoingPrintSource) => [
    "print.sources",
    [...(source.ParcelIds ?? [])].sort().join(","),
    [...(source.DocumentIds ?? [])].sort().join(","),
  ],
};

const getSettings = (): Promise<{ data: IPrintSettings }> =>
  axiosInstance.get("settings/print");

const usePrintSettings = (enabled = true) =>
  useCustomQuery(printQueryKeys.settings, getSettings, {
    enabled,
    staleTime: 60000,
  });

const saveFormat = (payload: ISavePrintFormat): Promise<{ data: IPrintFormat }> =>
  axiosInstance.post("settings/print/formats", payload);

const useSaveFormat = () => useCustomMutation(saveFormat);

const removeFormat = (id: string) =>
  axiosInstance.delete(`settings/print/formats/${id}`);

const useRemoveFormat = () => useCustomMutation(removeFormat);

const saveProfile = (payload: ISavePrintProfile): Promise<{ data: IPrintProfile }> =>
  axiosInstance.post("settings/print/profiles", payload);

const useSaveProfile = () => useCustomMutation(saveProfile);

const removeProfile = (id: string) =>
  axiosInstance.delete(`settings/print/profiles/${id}`);

const useRemoveProfile = () => useCustomMutation(removeProfile);

const getSources = (
  source: IOutgoingPrintSource
): Promise<{ data: IOutgoingPrintSources }> =>
  axiosInstance.post("outgoingParcel/print/sources", source);

const usePrintSources = (source: IOutgoingPrintSource, enabled = true) =>
  useCustomQuery(printQueryKeys.sources(source), () => getSources(source), {
    enabled:
      enabled &&
      ((source.ParcelIds?.length ?? 0) > 0 ||
        (source.DocumentIds?.length ?? 0) > 0),
    refetchOnWindowFocus: false,
  });

type ErrorBody = { errorCodes?: { message?: string }[]; Message?: string } | undefined;

const bodyMessage = (body: ErrorBody) =>
  body?.errorCodes?.[0]?.message ?? body?.Message ?? null;

/** Komunikat błędu (ErrorResponse: errorCodes[0].message) — także z odpowiedzi typu blob. */
export const printErrorMessage = async (e: unknown): Promise<string | null> => {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      return bodyMessage(JSON.parse(await data.text()));
    } catch {
      return null;
    }
  }
  return bodyMessage(data as ErrorBody);
};

/** PDF naklejek / kopert / zwrotek ZPO. Rzuca Error z komunikatem serwera. */
const renderPrint = async (request: IOutgoingPrintRenderRequest): Promise<Blob> => {
  try {
    const response = await axiosInstance.post("outgoingParcel/print/render", request, {
      responseType: "blob",
    });
    return new Blob([response.data], { type: "application/pdf" });
  } catch (e) {
    throw new Error((await printErrorMessage(e)) ?? "Nie udało się przygotować wydruku.");
  }
};

export default {
  usePrintSettings,
  useSaveFormat,
  useRemoveFormat,
  useSaveProfile,
  useRemoveProfile,
  usePrintSources,
  getSources,
  renderPrint,
};
